import { useEffect, useState } from 'react';
import React from 'react';
import { formatISO } from 'date-fns';

import { Drawer, DrawerController } from '@reckon-web/drawer';
import { Stack } from '@reckon-web/stack';
import { Checkbox } from '@reckon-web/checkbox';
import { Notice } from '@reckon-web/notice';
import { Text } from '@reckon-web/text';
import { Box } from '@reckon-web/box';
import {
  field,
  formStateToFormValue,
  getInitialState,
  isFormValueEqualToValue,
  resetForm,
  useForm,
  useSubmit,
  validate,
} from '@reckon-web/forms';
import { BaseOption } from '@reckon-web/autocomplete';
import { useToasts } from '@reckon-web/toast';
import { makeId } from '@reckon-web/utils';

import { DatePickerField, ISODate } from '../../common/date-picker-field';
import { SelectField } from '../../common/select-field';
import {
  RecurrenceFrequencyEnum,
  RecurrenceFrequencyEnumValue,
} from '../../job/types/recurrence-frequency-enum';
import { useUpdateJobRecurrance } from '../../../data/useUpdateJobRecurrance';
import { useTenantTimezoneConversion } from '../../../hooks/use-tenant-timezone';
import { Job } from '../../../hooks/use-query-job';
import { JobRecurrence } from '../../../hooks/job-recurrences/use-query-job-recurrences';

interface IRescheduleDrawerProps {
  reset?: Symbol;
  drawerOpen: boolean;
  onClose: () => void;
  recurringJob: Job | JobRecurrence | null;
  testId?: string;
}

// // this can be called from a few spots now, and the results come from a variety of graphql calls
// // this type is a bit of a double up, but it's the only way to ensure proper type safety
// export type RecurringJob = {
//   cloudId: string;
//   name?: string | null;
//   recurrence: GetJobRecurrenceRecurrenceResult | undefined | null;
// };
const RecurrenceFrequency = RecurrenceFrequencyEnum.valuesEnum;

const recurrenceFrequencyOptions: BaseOption[] = [
  {
    label: 'Week',
    value: RecurrenceFrequency.SevenDays,
  },
  {
    label: 'Fortnight',
    value: RecurrenceFrequency.FourteenDays,
  },
  {
    label: '1 month',
    value: RecurrenceFrequency.OneMonth,
  },
  {
    label: '2 months',
    value: RecurrenceFrequency.TwoMonths,
  },
  {
    label: '3 months',
    value: RecurrenceFrequency.ThreeMonths,
  },
  {
    label: '6 months',
    value: RecurrenceFrequency.SixMonths,
  },
  {
    label: '12 months',
    value: RecurrenceFrequency.TwelveMonths,
  },
];

export const RescheduleDrawer = ({
  reset,
  drawerOpen,
  onClose,
  recurringJob,
  testId = `reschedule-drawer`,
}: IRescheduleDrawerProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [_updateJobRecurrance] = useUpdateJobRecurrance();
  const { addToast } = useToasts();
  const { localDateToTenantToUtc } = useTenantTimezoneConversion();

  const rescheduleForm = field.object(
    {
      enable: field.boolean(),
      date: field.date(),
      frequency: field.select()(),
    },
    {
      validate: {
        date: validate.object((value, objectValue) => {
          if (objectValue.enable && !value) {
            validate.error('Required');
          }
          return value;
        }),
        frequency: validate.object((value, objectValue) => {
          if (objectValue.enable && !value) {
            validate.error('Required');
          }
          return value;
        }),
      },
    }
  );

  const initialFormState = React.useMemo(() => {
    return {
      enable: recurringJob?.recurrence?.enabled,
      date:
        recurringJob?.recurrence?.nextDateUtc &&
        (formatISO(recurringJob?.recurrence?.nextDateUtc, {
          representation: 'date',
        }) as ISODate),
      frequency: recurringJob?.recurrence?.recurrenceFrequency,
    };
    // we want to rebuild the form state when the job changes
    // AND when we get the reset signal
    // but we don't actually need to use reset in here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurringJob, reset]);

  const form = useForm(rescheduleForm, initialFormState);

  const isFormEqual = isFormValueEqualToValue(
    form,
    formStateToFormValue(
      rescheduleForm,
      getInitialState(rescheduleForm, initialFormState)
    )
  );

  // couple of booleans for if things have changed from their original values
  // I feel that this could also be done looking at various touched/dirty/??? properties on the form
  // but, I know this will work
  const hasRecurrenceChanged = React.useMemo(() => {
    // only need to show the info if editing, not if creating or recurring
    if (!initialFormState) {
      return false;
    }

    return (
      (initialFormState.enable || false) !== form.value.enable ||
      (initialFormState.frequency || '') !== (form.value.frequency || '') ||
      (initialFormState.date || '') !== (form.value.date || '')
    );
  }, [
    initialFormState,
    form.value.enable,
    form.value.frequency,
    form.value.date,
  ]);

  const submit = useSubmit(form, async (value) => {
    if (value.enable && (!value.frequency || !value.date)) return;
    setIsSaving(true);
    const cloudId = recurringJob!.cloudId || '';
    const gqlResult = await _updateJobRecurrance({
      variables: {
        input: {
          cloudId,
          enabled: value.enable,
          recurrencefrequency:
            RecurrenceFrequency[
              value.frequency as RecurrenceFrequencyEnumValue
            ],
          nextDateUtc: localDateToTenantToUtc(value.date) || null, // parseISO(value.date),
        },
      },
    });

    if (
      gqlResult &&
      gqlResult.data &&
      gqlResult.data.updateJobRecurrance &&
      (!gqlResult.data.updateJobRecurrance.validationMessage ||
        gqlResult.data.updateJobRecurrance.validationMessage.length === 0)
    ) {
      addToast({
        title: 'Job successfully rescheduled',
        message: `${recurringJob?.name} has been successfully rescheduled.`,
        tone: 'positive',
      });
      setIsSaving(false);
      onClose();
      return;
    }

    addToast({
      title: 'Job has not been rescheduled',
      message:
        'Workflow failed to reschedule the job. Try again or contact support.',
      tone: 'critical',
    });
    setIsSaving(false);
  });

  useEffect(
    () => {
      resetForm(form, initialFormState);
    },
    // Mitchell says this is fine because the only thing resetForm uses is form.setState
    // which doesn't depend on anything other than the schema passed to useForm which won't change
    // ideally we could pass in form.setState(and then probably use form.setState directly) but
    // form.setState isn't technically stable(as in between renders, not stable as in API, it is a stable API)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialFormState]
  );

  return (
    <>
      <DrawerController isOpen={drawerOpen} data-testid={testId}>
        <Drawer
          type="form"
          title={`Reschedule ${recurringJob?.name}`}
          actions={{
            cancel: {
              label: 'Cancel',
              action: () => {
                onClose();
              },
            },
            confirm: {
              label: 'Save',
              action: () => {
                submit();
              },
              loading: isSaving,
            },
          }}
          shouldShowCancelConfirmationDialog={!isFormEqual}
          /* TODO Workflow-400 once balance supports test id's, replace this ID */
          id={makeId(testId, 'reschedule')}
        >
          <Stack gap="xlarge">
            <Checkbox
              checked={form.fields.enable.props.value}
              onChange={(e) =>
                form.fields.enable.props.onChange(e.target.checked)
              }
              data-testid={makeId(testId, 'checkbox')}
            >
              This job recurs
            </Checkbox>
            <DatePickerField
              disabled={!form.value.enable}
              label="Next instance..."
              {...form.fields.date.props}
              /* TODO Workflow-400 once balance supports test id's, replace this ID */
              id={makeId(testId, 'next-instance')}
            />
            <SelectField
              disabled={!form.value.enable}
              label="then every..."
              options={recurrenceFrequencyOptions}
              {...form.fields.frequency.props}
              /* TODO Workflow-400 once balance supports test id's, replace this ID */
              id={makeId(testId, 'then-every')}
            />
            {hasRecurrenceChanged && form.value.enable && (
              <Box marginBottom="small">
                <Notice tone="informative">
                  <Text size="small">
                    NOTE: when you recur this job, the due date on the job and
                    its tasks will be incremented by the recurrence period
                    above.
                  </Text>
                </Notice>
              </Box>
            )}
          </Stack>
        </Drawer>
      </DrawerController>
    </>
  );
};
